import * as maplibregl from "maplibre-gl";
import type { LngLat } from "../types";

const EARTH_CIRCUMFERENCE_M = 40_075_016.686;
const WORLD_TILE_SIZE_PX = 512;
const HEADING_MIN_CHANGE_DEG = 2;
const POSITION_GLIDE_MS = 800;
const STALE_AFTER_MS = 30_000;
const ACCURACY_MIN_RADIUS_PX = 12;
const ACCURACY_MAX_RADIUS_PX = 2000;
const ACCURACY_MAX_METERS = 100;

export function shortestAngleDelta(fromDeg: number, toDeg: number): number {
  return ((((toDeg - fromDeg) % 360) + 540) % 360) - 180;
}

export function metersToPixels(meters: number, latitudeDeg: number, zoom: number): number {
  const metersPerPixel = (EARTH_CIRCUMFERENCE_M * Math.cos((latitudeDeg * Math.PI) / 180)) / (WORLD_TILE_SIZE_PX * 2 ** zoom);
  return meters / metersPerPixel;
}

/** Où en est l'affichage du cap : le cap affiché, déroulé (il peut dépasser 360), et s'il faut le redessiner. */
export function nextDisplayedHeading(displayedDeg: number | null, headingDeg: number): number | null {
  if (displayedDeg == null) return headingDeg;
  const delta = shortestAngleDelta(displayedDeg, headingDeg);
  return Math.abs(delta) < HEADING_MIN_CHANGE_DEG ? null : displayedDeg + delta;
}

function createElement(className: string): HTMLDivElement {
  const element = document.createElement("div");
  element.className = className;
  return element;
}

/**
 * Position de l'utilisateur, dessinée par des éléments de la page posés sur la carte plutôt que par
 * un calque : la boussole émet des dizaines de caps par seconde, et faire tourner un élément ne
 * redessine pas la carte, quand mettre à jour une source la redessinerait entière à chaque fois.
 */
export class UserLocationMarker {
  private readonly ground: maplibregl.Marker;
  private readonly dot: maplibregl.Marker;
  private readonly accuracy: HTMLDivElement;
  private readonly beam: HTMLDivElement;
  private displayed: LngLat | null = null;
  private target: LngLat | null = null;
  private accuracyMeters = 0;
  private displayedHeading: number | null = null;
  private glideFrame: number | null = null;
  private headingFrame: number | null = null;
  private staleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly handleZoom = () => this.resizeAccuracy();

  constructor(private readonly map: maplibregl.Map) {
    const groundElement = createElement("toka-user-location");
    this.accuracy = createElement("toka-user-location__accuracy");
    this.beam = createElement("toka-user-location__beam");
    groundElement.append(this.accuracy, this.beam);
    this.ground = new maplibregl.Marker({ element: groundElement, rotationAlignment: "map", pitchAlignment: "map" });
    this.dot = new maplibregl.Marker({
      element: createElement("toka-user-location__dot"),
      rotationAlignment: "viewport",
      pitchAlignment: "viewport",
    });
    map.on("zoom", this.handleZoom);
  }

  setPosition(position: LngLat, accuracyMeters: number): void {
    this.target = position;
    this.accuracyMeters = accuracyMeters;
    this.markFresh();
    if (!this.displayed) {
      this.moveTo(position);
      this.ground.setLngLat(position).addTo(this.map);
      this.dot.setLngLat(position).addTo(this.map);
    } else {
      this.glide(this.displayed, position);
    }
    this.resizeAccuracy();
  }

  setHeading(headingDeg: number | null): void {
    if (headingDeg == null) {
      this.displayedHeading = null;
      this.beam.hidden = true;
      return;
    }
    const next = nextDisplayedHeading(this.displayedHeading, headingDeg);
    if (next == null) return;
    this.displayedHeading = next;
    if (this.headingFrame != null) return;
    this.headingFrame = requestAnimationFrame(() => {
      this.headingFrame = null;
      this.beam.hidden = false;
      this.beam.style.transform = `rotate(${this.displayedHeading}deg)`;
    });
  }

  remove(): void {
    this.map.off("zoom", this.handleZoom);
    if (this.glideFrame != null) cancelAnimationFrame(this.glideFrame);
    if (this.headingFrame != null) cancelAnimationFrame(this.headingFrame);
    if (this.staleTimer != null) clearTimeout(this.staleTimer);
    this.ground.remove();
    this.dot.remove();
  }

  private glide(from: LngLat, to: LngLat): void {
    if (this.glideFrame != null) cancelAnimationFrame(this.glideFrame);
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / POSITION_GLIDE_MS);
      const eased = 1 - (1 - progress) ** 3;
      this.moveTo([from[0] + (to[0] - from[0]) * eased, from[1] + (to[1] - from[1]) * eased]);
      this.glideFrame = progress < 1 ? requestAnimationFrame(step) : null;
    };
    this.glideFrame = requestAnimationFrame(step);
  }

  private moveTo(position: LngLat): void {
    this.displayed = position;
    this.ground.setLngLat(position);
    this.dot.setLngLat(position);
  }

  private resizeAccuracy(): void {
    if (!this.target) return;
    const radius = metersToPixels(this.accuracyMeters, this.target[1], this.map.getZoom());
    this.accuracy.hidden = radius < ACCURACY_MIN_RADIUS_PX || this.accuracyMeters > ACCURACY_MAX_METERS;
    const size = 2 * Math.min(radius, ACCURACY_MAX_RADIUS_PX);
    this.accuracy.style.width = `${size}px`;
    this.accuracy.style.height = `${size}px`;
  }

  private markFresh(): void {
    this.setStale(false);
    if (this.staleTimer != null) clearTimeout(this.staleTimer);
    this.staleTimer = setTimeout(() => this.setStale(true), STALE_AFTER_MS);
  }

  private setStale(stale: boolean): void {
    this.ground.getElement().classList.toggle("toka-user-location--stale", stale);
    this.dot.getElement().classList.toggle("toka-user-location__dot--stale", stale);
  }
}
